import * as Sentry from '@sentry/react';
import { IndexeddbPersistence, storeState } from 'y-indexeddb';
import * as Y from 'yjs';
import type { PersistenceAdapter } from './persistence-adapter';
import { setWorkspaceStatus, getWorkspaceStatus } from './hooks/use-workspace-status';

export class LocalPersistence implements PersistenceAdapter {
  private providers = new Map<string, IndexeddbPersistence>();
  // In-flight destroy() promises, keyed by entityId. A new provider for the same
  // IndexedDB database must NOT be opened until the previous one's destroy (which
  // closes the DB connection asynchronously) has resolved — otherwise IndexedDB
  // blocks the re-open and whenSynced never resolves (persistence-timeout).
  private pendingDestroys = new Map<string, Promise<void>>();
  private dbName = 'nula-sync';
  private degraded = false;

  bindDoc(entityId: string, doc: Y.Doc): IndexeddbPersistence {
    const existing = this.providers.get(entityId);
    if (existing) return existing;
    const provider = new IndexeddbPersistence(`${this.dbName}-${entityId}`, doc);
    this.providers.set(entityId, provider);
    return provider;
  }

  async bindDocAndSync(entityId: string, doc: Y.Doc): Promise<void> {
    try {
      // Serialize close-then-open for the same DB: wait for any in-flight destroy
      // to finish before opening a new provider for this entityId.
      const pending = this.pendingDestroys.get(entityId);
      if (pending) await pending;
      const provider = this.bindDoc(entityId, doc);
      if (!provider.synced) {
        await Promise.race([
          provider.whenSynced,
          new Promise<never>((_, rej) =>
            setTimeout(() => rej(new Error('persistence-timeout')), 10000),
          ),
        ]);
      }
    } catch (err) {
      if (this.isQuotaError(err)) {
        this.handleQuotaExceeded(err);
      } else {
        Sentry.captureException(err);
        console.warn(`[LocalPersistence] bind failed for ${entityId}:`, err);
      }
    }
  }

  isDegraded(): boolean {
    return this.degraded;
  }

  private isQuotaError(err: unknown): boolean {
    if (err instanceof DOMException && err.name === 'QuotaExceededError') return true;
    const msg = String(err);
    return msg.includes('QuotaExceeded') || msg.toLowerCase().includes('quota');
  }

  private handleQuotaExceeded(err: unknown): void {
    if (this.degraded) return;
    this.degraded = true;
    Sentry.captureException(err);
    console.error(
      '[LocalPersistence] IndexedDB quota exceeded — running in memory-only mode',
      err,
    );
    const current = getWorkspaceStatus();
    if (current.phase === 'local-ready' || current.phase === 'sync-complete') {
      setWorkspaceStatus({ ...current, persistence: 'degraded' });
    }
  }

  unbindDoc(entityId: string): Promise<void> {
    const provider = this.providers.get(entityId);
    if (!provider) return Promise.resolve();
    {
      // Flush before tearing down. y-indexeddb debounces its compacting store and
      // destroy() CANCELS the pending store + closes the DB without writing it —
      // so an edit made just before teardown (e.g. inserting a canvas embed then
      // navigating away within the registry grace window) is silently lost. Force
      // a store first, then destroy once it has been written.
      const flushed = Promise.resolve(storeState(provider, true)).catch(() => {
        // A failed flush must not block teardown — destroy still has to run.
      });
      // destroy() closes the IndexedDB connection asynchronously. Track the
      // promise so a re-bind (bindDocAndSync) for the same entityId waits for the
      // flush AND close before re-opening — otherwise the re-open races the close
      // and hangs (persistence-timeout).
      const destroyed = flushed.then(() => provider.destroy()).finally(() => {
        // Only clear if this is still the tracked destroy (a later unbind would
        // have replaced it).
        if (this.pendingDestroys.get(entityId) === destroyed) {
          this.pendingDestroys.delete(entityId);
        }
      });
      this.pendingDestroys.set(entityId, destroyed);
      this.providers.delete(entityId);
      return destroyed;
    }
  }

  async overwriteYjsState(entityId: string, doc: Y.Doc): Promise<void> {
    // Unbind any existing provider for this doc first
    this.unbindDoc(entityId);
    // Delete the existing IndexedDB database
    const dbName = `${this.dbName}-${entityId}`;
    await new Promise<void>((resolve, reject) => {
      const req = indexedDB.deleteDatabase(dbName);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
    // Bind the new doc to IndexedDB — this writes the doc's current state
    const provider = new IndexeddbPersistence(dbName, doc);
    await provider.whenSynced;
    provider.destroy();
  }

  async deleteYjsState(entityId: string): Promise<void> {
    const dbName = `${this.dbName}-${entityId}`;
    try {
      await new Promise<void>((resolve, reject) => {
        const req = indexedDB.deleteDatabase(dbName);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      });
    } finally {
      this.unbindDoc(entityId);
    }
  }
}
